// src/features/auth/components/PasswordResetModal.tsx
import React, { useState } from 'react';
import { XMarkIcon, EnvelopeIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import authService from '../services/authService';
import RateLimitFeedback from './RateLimitFeedback';
import { authErrorHandlers, AuthError, isRateLimitError } from '../utils/authErrorUtils';
import { AUTH_MESSAGES } from '../constants/messages';

interface PasswordResetModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PasswordResetModal: React.FC<PasswordResetModalProps> = ({
  isOpen,
  onClose
}) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [authError, setAuthError] = useState<AuthError | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    setAuthError(null);

    try {
      await authService.requestPasswordReset({ email });
      setSuccess(true);
    } catch (err: any) {
      const error = authErrorHandlers.passwordReset(err);
      setAuthError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setSuccess(false);
    setAuthError(null);
    onClose();
  };

  const handleRateLimitRetry = () => {
    setAuthError(null);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-neutral-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-theme-bg dark:bg-dark-theme-bg rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full sm:p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-theme-text dark:text-dark-theme-text">
              {AUTH_MESSAGES.PASSWORD_RESET.MODAL_TITLE}
            </h3>
            <button
              onClick={handleClose}
              className="text-theme-text-secondary dark:text-dark-theme-text-secondary hover:text-theme-text dark:hover:text-dark-theme-text"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {success ? (
            /* Success State */
            <div className="text-center space-y-4">
              <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto" />
              <div>
                <h4 className="text-lg font-medium text-theme-text dark:text-dark-theme-text mb-2">
                  {AUTH_MESSAGES.PASSWORD_RESET.EMAIL_SENT_TITLE}
                </h4>
                <p className="text-sm text-theme-text-secondary dark:text-dark-theme-text-secondary">
                  We've sent password reset instructions to <strong>{email}</strong>
                </p>
              </div>
              <button
                onClick={handleClose}
                className="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-button hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light dark:focus:ring-primary-dark transition-opacity duration-150"
              >
                {AUTH_MESSAGES.BUTTONS.DONE}
              </button>
            </div>
          ) : (
            /* Form State */
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <p className="text-sm text-theme-text-secondary dark:text-dark-theme-text-secondary mb-4">
                  {AUTH_MESSAGES.PASSWORD_RESET.MODAL_DESCRIPTION}
                </p>
                
                <label htmlFor="reset-email" className="sr-only">
                  {AUTH_MESSAGES.LABELS.EMAIL}
                </label>
                <div className="relative">
                  <input
                    id="reset-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="appearance-none block w-full pl-10 pr-3 py-2 border border-theme-border dark:border-dark-theme-border rounded-md shadow-sm placeholder-theme-text-tertiary dark:placeholder-dark-theme-text-tertiary focus:outline-none focus:ring-primary-light dark:focus:ring-primary-dark focus:border-primary-light dark:focus:border-primary-dark sm:text-sm bg-theme-bg dark:bg-dark-theme-bg text-theme-text dark:text-dark-theme-text"
                    placeholder={AUTH_MESSAGES.PLACEHOLDERS.EMAIL}
                  />
                  <EnvelopeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-theme-text-tertiary dark:text-dark-theme-text-tertiary" />
                </div>
              </div>

              {/* Rate Limit Feedback */}
              {authError && isRateLimitError(authError) && (
                <RateLimitFeedback
                  rateLimitInfo={authError.rateLimitInfo!}
                  onRetryAvailable={handleRateLimitRetry}
                />
              )}

              {/* General Error Display */}
              {authError && !isRateLimitError(authError) && (
                <div className={`p-3 rounded-md ${
                  authError.type === 'validation'
                    ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800'
                    : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                }`}>
                  <p className={`text-sm ${
                    authError.type === 'validation'
                      ? 'text-amber-700 dark:text-amber-300'
                      : 'text-red-700 dark:text-red-300'
                  }`}>
                    {authError.message}
                  </p>
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 py-2 px-4 border border-theme-border dark:border-dark-theme-border rounded-md text-sm font-medium text-theme-text dark:text-dark-theme-text hover:bg-theme-bg-secondary dark:hover:bg-dark-theme-bg-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light dark:focus:ring-primary-dark transition-colors duration-150"
                >
                  {AUTH_MESSAGES.BUTTONS.CANCEL}
                </button>
                <button
                  type="submit"
                  disabled={loading || !email.trim()}
                  className="flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-button hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light dark:focus:ring-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-opacity duration-150"
                >
                  {loading ? AUTH_MESSAGES.PASSWORD_RESET.SENDING : AUTH_MESSAGES.BUTTONS.SEND_RESET_EMAIL}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default PasswordResetModal;
